import { ConfluenceWorkspaceAri } from '@atlassian/ari/confluence';

import { buildContentAri, type BylineContentType } from '../utils/contentAri.ts';
import {
	buildRelatedContentCypher,
	MAX_RELATED_CONTENT,
} from '../utils/relatedContentRelationships.ts';
import type { BylineAppId } from '../utils/bylineAppIdentity.ts';
import type {
	useRelatedContentQuery$data,
	useRelatedContentQuery$variables,
} from '../hooks/__generated__/useRelatedContentQuery.graphql.ts';

import {
	readGraphSampleAppIds,
	readGraphSampleRowCounts,
	type GraphSampleRowCounts,
} from './graphSampleAppIds.ts';

export type RelatedContentSsrStatus = 'present' | 'empty' | 'unavailable';

// `count` (sampled rows, at most `RELATED_CONTENT_SAMPLE_LIMIT`) exists only on the 'present' variant
// so the SSR placeholder can add it to the other sources when deciding whether the page reaches the
// byline's row minimum (CCINT-9359). `appIds` are the distinct apps the hydrated rows identified,
// in result order, which size the placeholder (CCINT-9543).
export type RelatedContentSsrState = Readonly<
	| { contentId: string; status: 'present'; count: number; appIds: readonly BylineAppId[] }
	| { contentId: string; status: 'empty' | 'unavailable' }
>;

export type RelatedContentSampleUnavailableReason =
	| 'invalid-input'
	| 'malformed-response'
	| 'query-error';

// Only the row count leaves this layer; the cached query holds the entity ARIs like any other
// preloaded query.
export type RelatedContentSampleResult =
	| Readonly<{
			status: 'available';
			count: number;
			appIds: readonly BylineAppId[];
			rows: GraphSampleRowCounts;
	  }>
	| Readonly<{ status: 'unavailable'; reason: RelatedContentSampleUnavailableReason }>;

// The placeholder only needs to know whether the byline has at least three rows.
export const RELATED_CONTENT_SAMPLE_LIMIT = 3;

const RELATED_CONTENT_COLUMN = 'relatedContent';

export function createRelatedContentSsrState(
	contentId: string,
	status: 'empty' | 'unavailable',
): RelatedContentSsrState;
export function createRelatedContentSsrState(
	contentId: string,
	status: 'present',
	count: number,
	appIds?: readonly BylineAppId[],
): RelatedContentSsrState;
export function createRelatedContentSsrState(
	contentId: string,
	status: RelatedContentSsrStatus,
	count?: number,
	appIds: readonly BylineAppId[] = [],
): RelatedContentSsrState {
	if (status === 'present') {
		return Object.freeze({
			contentId,
			status,
			count: count ?? 0,
			appIds: Object.freeze([...appIds]),
		});
	}
	return Object.freeze({ contentId, status });
}

export const buildRelatedContentSampleVariables = (
	cloudId: string,
	activationId: string,
	contentId: string,
	contentType?: BylineContentType,
): useRelatedContentQuery$variables | undefined => {
	if (!cloudId.trim() || !activationId.trim() || !/^[1-9]\d*$/.test(contentId)) {
		return undefined;
	}

	let pageAri: string;
	let queryContext: string;
	try {
		pageAri = buildContentAri(cloudId, contentId, contentType);
		queryContext = ConfluenceWorkspaceAri.create({ siteId: cloudId, activationId }).toString();
	} catch {
		return undefined;
	}

	return {
		cypherQuery: buildRelatedContentCypher(pageAri),
		queryContext,
		version: 'V3',
		first: MAX_RELATED_CONTENT,
	};
};

// Pure normalization of the bounded sample. Defensive against a malformed shape because the response
// is an untyped Cypher projection. Only the row count leaves this function.
export const normalizeRelatedContentSampleData = (
	data: useRelatedContentQuery$data | null | undefined,
): RelatedContentSampleResult => {
	const edges = data?.graphStore_cypherQueryV2?.edges;
	if (!edges || edges.length > MAX_RELATED_CONTENT) {
		return { status: 'unavailable', reason: 'malformed-response' };
	}

	for (const edge of edges) {
		const columns = edge.node.columns;
		if (columns.length !== 1) {
			return { status: 'unavailable', reason: 'malformed-response' };
		}

		const column = columns[0];
		if (
			column.key !== RELATED_CONTENT_COLUMN ||
			column.value?.__typename !== 'GraphStoreCypherQueryV2AriNode' ||
			typeof column.value.id !== 'string' ||
			!column.value.id.startsWith('ari:')
		) {
			return { status: 'unavailable', reason: 'malformed-response' };
		}
	}

	return {
		status: 'available',
		count: Math.min(edges.length, RELATED_CONTENT_SAMPLE_LIMIT),
		appIds: readGraphSampleAppIds(edges),
		rows: readGraphSampleRowCounts(edges),
	};
};

// Maps a query response (from the network preload or the hydrated cache) to the terminal SSR state.
export const normalizeRelatedContentSsrState = (
	contentId: string,
	data: useRelatedContentQuery$data | null | undefined,
): RelatedContentSsrState => {
	const result = normalizeRelatedContentSampleData(data);
	if (result.status === 'unavailable') {
		return createRelatedContentSsrState(contentId, 'unavailable');
	}
	return result.count > 0
		? createRelatedContentSsrState(contentId, 'present', result.count, result.appIds)
		: createRelatedContentSsrState(contentId, 'empty');
};
